import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
	fetchInventaireCategories,
	fetchInventaireArticles,
} from "../../../store/actions/atelierInventaireActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import Loader from "../../basics/Loader"
import CategorieModalV2 from "./CategorieModalV2"
import ArticleModalV2 from "./ArticleModalV2"
import OptionsStockVirtuelV2 from "./OptionsStockVirtuelV2"

const SUBTABS = [
	{ key: "stock", label: "Stock" },
	{ key: "stockVirtuel", label: "Stock virtuel" },
]

const fmt2 = (v) => {
	const n = Number(v)
	return Number.isNaN(n) ? v : n.toFixed(2)
}

const CategorieRow = ({ categorie, index, selected, onSelect, onEdit }) => {
	const [hover, setHover] = useState(false)
	const bg = selected
		? COLORS.accentLight
		: hover
			? tableStyles.rowHover
			: tableStyles.rowBg(index)
	return (
		<tr
			onClick={() => onSelect(categorie)}
			onDoubleClick={() => onEdit(categorie)}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			<td
				style={{
					...tableStyles.td,
					fontWeight: selected ? 600 : 500,
					color: selected ? COLORS.accent : COLORS.text,
				}}
			>
				{categorie.nom || "—"}
			</td>
		</tr>
	)
}

const ArticleRow = ({ article, index, isBoss, onEdit }) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	return (
		<tr
			onDoubleClick={() => onEdit(article)}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			<td style={{ ...tableStyles.td, fontWeight: 600 }}>
				{article.nom || "—"}
			</td>
			<td style={tableStyles.tdRight}>{fmt2(article.prixVente)}</td>
			<td style={tableStyles.tdRight}>{article.limite}</td>
			{isBoss ? (
				<td style={tableStyles.tdRight}>{fmt2(article.commission)}</td>
			) : null}
		</tr>
	)
}

// Sous-onglet "Stock" : maître-détail catégories → articles. Réutilise l'infra
// Redux existante.
const OptionsStockV2 = () => {
	const dispatch = useDispatch()
	const categories = useSelector((s) => s.atelierInventaire.categories)
	const fetchCategories = useSelector(
		(s) => s.atelierInventaire.fetchCategories,
	)
	const articles = useSelector((s) => s.atelierInventaire.articles)
	const fetchArticles = useSelector((s) => s.atelierInventaire.fetchArticles)
	const isBoss = useSelector((s) =>
		s.navInfo.login.groups.includes("boss"),
	)

	const [selectedCatId, setSelectedCatId] = useState(null)
	const [editingCat, setEditingCat] = useState(null)
	const [editingArticle, setEditingArticle] = useState(null)

	useEffect(() => {
		dispatch(fetchInventaireCategories())
	}, [dispatch])

	const sortedCats = [...(categories || [])].sort((a, b) =>
		(a.nom || "").localeCompare(b.nom || "", "fr"),
	)
	// Catégorie sélectionnée dérivée de l'id → reflète renommage/suppression.
	const selectedCat =
		sortedCats.find((c) => c.id === selectedCatId) || null

	const selectCat = (cat) => {
		setSelectedCatId(cat.id)
		dispatch(fetchInventaireArticles(cat.id))
	}

	const catsLoading = fetchCategories.loading && sortedCats.length === 0

	return (
		<>
			{editingCat && (
				<CategorieModalV2
					categorie={editingCat}
					onClose={() => setEditingCat(null)}
				/>
			)}
			{editingArticle && (
				<ArticleModalV2
					article={editingArticle}
					categorieId={selectedCatId}
					onClose={() => setEditingArticle(null)}
				/>
			)}

			<div style={s.wrap}>
				{/* ───── Catégories ───── */}
				<div style={s.leftCol}>
					<div style={s.panelHead}>
						<span style={s.panelTitle}>Catégories</span>
						<button
							style={gs.btnPrimary}
							onClick={() => setEditingCat({})}
						>
							+ Catégorie
						</button>
					</div>
					<div style={tableStyles.container}>
						{catsLoading ? (
							<Loader size={28} padding={24} />
						) : sortedCats.length === 0 ? (
							<div style={tableStyles.empty}>Aucune catégorie.</div>
						) : (
							<table style={tableStyles.table}>
								<tbody>
									{sortedCats.map((cat, i) => (
										<CategorieRow
											key={cat.id}
											categorie={cat}
											index={i}
											selected={cat.id === selectedCatId}
											onSelect={selectCat}
											onEdit={setEditingCat}
										/>
									))}
								</tbody>
							</table>
						)}
					</div>
				</div>

				{/* ───── Articles de la catégorie ───── */}
				<div style={s.rightCol}>
					{!selectedCat ? (
						<div style={s.placeholder}>
							Sélectionnez une catégorie pour voir ses articles.
						</div>
					) : (
						<>
							<div style={s.panelHead}>
								<span style={s.panelTitle}>
									Articles · {selectedCat.nom}
								</span>
								<button
									style={gs.btnPrimary}
									onClick={() => setEditingArticle({})}
								>
									+ Article
								</button>
							</div>
							<div style={tableStyles.container}>
								{fetchArticles.loading ? (
									<Loader size={28} padding={24} />
								) : (articles || []).length === 0 ? (
									<div style={tableStyles.empty}>
										Aucun article.
									</div>
								) : (
									<table style={tableStyles.table}>
										<thead>
											<tr>
												<th style={tableStyles.th}>Nom</th>
												<th style={tableStyles.thRight}>
													Prix vente
												</th>
												<th style={tableStyles.thRight}>
													Limite
												</th>
												{isBoss ? (
													<th style={tableStyles.thRight}>
														Commission
													</th>
												) : null}
											</tr>
										</thead>
										<tbody>
											{articles.map((art, i) => (
												<ArticleRow
													key={art.id}
													article={art}
													index={i}
													isBoss={isBoss}
													onEdit={setEditingArticle}
												/>
											))}
										</tbody>
									</table>
								)}
							</div>
						</>
					)}
				</div>
			</div>
		</>
	)
}

// Onglet "Options" de l'inventaire V2 (chef_atelier) : deux sous-onglets Stock
// (catégories → articles) et Stock virtuel (consommables → consommations).
const OptionsV2 = () => {
	const [subtab, setSubtab] = useState("stock")
	return (
		<>
			<div style={gs.sectionTabs}>
				{SUBTABS.map((t) => (
					<button
						key={t.key}
						type="button"
						style={gs.sectionTab(subtab === t.key)}
						onClick={() => setSubtab(t.key)}
					>
						{t.label}
					</button>
				))}
			</div>
			{subtab === "stock" ? <OptionsStockV2 /> : <OptionsStockVirtuelV2 />}
		</>
	)
}

const s = {
	wrap: { display: "flex", gap: 20, alignItems: "flex-start" },
	leftCol: { width: 280, flexShrink: 0 },
	rightCol: { flex: 1, minWidth: 0 },
	panelHead: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 12,
		gap: 10,
	},
	panelTitle: { fontSize: 15, fontWeight: 600, color: COLORS.text },
	placeholder: {
		padding: "60px 16px",
		textAlign: "center",
		color: COLORS.textLight,
		fontSize: 14,
		backgroundColor: COLORS.surface,
		borderRadius: 14,
		boxShadow:
			"0 1px 2px rgba(28, 25, 23, 0.04), 0 6px 20px rgba(28, 25, 23, 0.06)",
	},
}

export default OptionsV2
